import type { BadgeTone } from "@/components/ui/Badge";
import { buildManufacturingWorkOrderDetailPath } from "@/lib/hubEntityDetailPaths";
import {
  unifiedWorkOrderStatusBadgeTone,
  unifiedWorkOrderStatusFromRaw,
  unifiedWorkOrderStatusFromRow,
  unifiedWorkOrderStatusLabel,
  type UnifiedWorkOrderStatus,
  type WorkOrderStatusSourceRow,
} from "@integrator-src/manufacturing/work-order-status-unified";

export type { UnifiedWorkOrderStatus, WorkOrderStatusSourceRow };

export interface WorkOrderListRow {
  id: number;
  number: string;
  mv_id: number | null;
  mv_number: string | null;
  mv_reference_number: string | null;
  mv_status: string | null;
  mv_ordered_quantity: number | null;
  mv_received_quantity: number | null;
  sku: string | null;
  quantity: number;
  mfr_reference_number: string | null;
  notes: string | null;
  stocking: boolean;
  rebuild: boolean;
  qc_type: string | null;
  location_id: number | null;
  location_name: string | null;
  location_megaventory_id: number | null;
  mrp_site_code: string | null;
  bom_line_count: number;
  bom_total_remaining: number;
  updated_at: string | null;
  mv_total_overhead_cost?: number | null;
  mv_total_labour_cost?: number | null;
  /** Distinct manufacturing batch ids linked through `public.batch_item.build_order_id`. */
  batch_ids?: number[];
  /** Present once the API and database migration include labor columns; UI tolerates omission. */
  labor_tracked_seconds?: number;
  labor_tracked_internal_dollars?: number | null;
  labor_applied_internal_dollars?: number;
  labor_unapplied_internal_dollars?: number | null;
  labor_internal_rates_configured?: boolean;
  execution_mode?: string | null;
  hub_status?: string | null;
  hub_ordered_quantity?: number | null;
  hub_received_quantity?: number | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancelled_by_email?: string | null;
  cancelled_by_display_name?: string | null;
  cancellation_reason?: string | null;
  cm_po_id?: number | null;
  cm_po_number?: string | null;
  cm_link_labour_cost?: number | null;
  cm_external_labour_total?: number | null;
  po_line_unit_price?: number | null;
  po_line_extended_cost?: number | null;
  external_labour_locked?: boolean;
  external_labour_charges_editable?: boolean;
  external_labour_charges?: WorkOrderExternalLabourCharge[];
  /** @deprecated use external_labour_charges_editable */
  external_labour_editable?: boolean;
}

export interface WorkOrderExternalLabourCharge {
  id: number;
  cm_po_id: number;
  cm_po_number: string;
  link_role: "primary" | "additional";
  labour_cost: number;
  purchase_order_item_id: number | null;
  po_line_unit_price: number | null;
  description: string | null;
}

export interface WorkOrderBomLine {
  id: number;
  componentSku: string;
  locationCode: string;
  quantityPerFinishedGood: number;
  quantityOrdered: number;
  quantityAllocated: number;
  quantityRemaining: number;
  quantityConsumed?: number;
  expectedConsumeDate: string | null;
  mvProductType?: string | null;
}

export interface WorkOrderDetail extends WorkOrderListRow {
  bom: WorkOrderBomLine[];
}

export interface WorkOrderListResponse {
  workOrders: WorkOrderListRow[];
  generatedAt: string;
  totalRows: number;
  limitApplied: number;
  summary: {
    activeNotTerminalCount: number;
    pendingCount: number;
    receivableCount: number;
  };
}

export interface RebuildResponse {
  ok: true;
  workOrder: WorkOrderDetail;
  oldMvId: number;
  newMvId: number;
  newMvNumber: string;
  bomReverted: boolean;
  warning: string | null;
  message: string;
}

export interface RebuildRequestSummary {
  id: string;
  build_order_id: number;
  status: string;
  reason: string | null;
  source: string;
  requested_by_user_id: string;
  requested_at: string;
  expires_at: string;
  mv_number: string | null;
  sku: string | null;
}

export type StatusFilter = "active" | "all" | "closed" | "cancelled";
/** `any` or a `public.location.id` (string). Legacy MRP codes still work on the API. */
export type LocationFilter = "any" | string;

export const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

export function statusTone(status: string | null): BadgeTone {
  return unifiedWorkOrderStatusBadgeTone(unifiedWorkOrderStatusFromRaw(status));
}

export function statusToneForWorkOrder(row: WorkOrderStatusSourceRow): BadgeTone {
  return unifiedWorkOrderStatusBadgeTone(unifiedWorkOrderStatusFromRow(row));
}

export function unifiedWorkOrderStatus(row: WorkOrderStatusSourceRow): UnifiedWorkOrderStatus {
  return unifiedWorkOrderStatusFromRow(row);
}

export function isHubNativeWorkOrder(row: Pick<WorkOrderListRow, "execution_mode">): boolean {
  return (row.execution_mode ?? "").trim() === "hub_native";
}

export function hubNativeWorkOrderCancelDisabledReason(
  row: Pick<
    WorkOrderListRow,
    | "execution_mode"
    | "hub_status"
    | "hub_received_quantity"
    | "quantity"
    | "batch_ids"
    | "labor_tracked_seconds"
  >,
): string | null {
  if (!isHubNativeWorkOrder(row)) {
    return "Only hub-native work orders can be cancelled in the hub.";
  }
  const hubStatus = (row.hub_status ?? "").trim().toLowerCase();
  if (hubStatus === "cancelled") {
    return "Work order is already cancelled.";
  }
  if (!["draft", "open", "in_process", "partially_received"].includes(hubStatus)) {
    return "This work order is not open.";
  }
  const receivedQuantity = Math.max(0, Math.round(Number(row.hub_received_quantity ?? 0)));
  if (receivedQuantity > 0) {
    return "Cannot cancel after finished goods have been received.";
  }
  if ((row.batch_ids ?? []).length > 0) {
    return "Cannot cancel a work order linked to manufacturing batches.";
  }
  if ((row.labor_tracked_seconds ?? 0) > 0) {
    return "Cannot cancel after floor labor scans were recorded.";
  }
  return null;
}

export function workOrderExecutionModeLabel(row: Pick<WorkOrderListRow, "execution_mode">): string {
  return isHubNativeWorkOrder(row) ? "Hub-native" : "Legacy (Megaventory)";
}

export function effectiveWorkOrderStatus(row: WorkOrderStatusSourceRow): string {
  return unifiedWorkOrderStatusLabel(unifiedWorkOrderStatusFromRow(row));
}

export function effectiveWorkOrderOrderedQuantity(row: WorkOrderListRow): number {
  if (isHubNativeWorkOrder(row)) {
    return Math.max(0, Math.round(Number(row.hub_ordered_quantity ?? row.quantity ?? 0)));
  }
  return Math.max(0, Math.round(Number(row.mv_ordered_quantity ?? row.quantity ?? 0)));
}

export function effectiveWorkOrderReceivedQuantity(row: WorkOrderListRow): number {
  if (isHubNativeWorkOrder(row)) {
    return Math.max(0, Math.round(Number(row.hub_received_quantity ?? 0)));
  }
  return Math.max(0, Math.round(Number(row.mv_received_quantity ?? 0)));
}

export function formatCount(value: number | null | undefined): string {
  if (value == null) return "—";
  return Math.trunc(value).toLocaleString();
}

export function formatBomEachQuantity(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 1000) / 1000;
  if (Math.abs(rounded - Math.trunc(rounded)) < 1e-9) {
    return Math.trunc(rounded).toLocaleString();
  }
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

export function workOrderHasOpenMaterialDemand(bom: WorkOrderBomLine[]): boolean {
  return bom.some((line) => line.quantityRemaining > 0);
}

export function formatDateTime(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatUsdTwoDecimals(value: number | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return numeric.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatExternalLabourPerUnit(
  labourCost: number | null | undefined,
  quantity: number | null | undefined,
): string {
  if (labourCost == null) {
    return "—";
  }
  const total = Number(labourCost);
  const qty = Math.max(0, Math.round(Number(quantity ?? 0)));
  if (!Number.isFinite(total) || qty <= 0) {
    return "—";
  }
  return `${formatUsdTwoDecimals(total / qty)} / unit`;
}

export const INTERNAL_LABOR_COLUMN_TITLE =
  "Applied: Megaventory internal total overhead on this work order (labor plus overhead). Unapplied: floor scan time from labor.device_scan, bucketed by UTC calendar month, valued using that month's labor plus overhead dollars per hour from Manufacturing settings, minus applied.";

export const HUB_NATIVE_INTERNAL_LABOR_COLUMN_TITLE =
  "Applied: internal labor dollars posted on Megaventory receipt sub-work-orders for this hub work order (applied at inventory receipt). Unapplied: floor scan time valued from Manufacturing settings rates, minus applied receipt dollars.";

export function formatInternalLaborAppliedUnapplied(row: WorkOrderListRow): { label: string; title: string } {
  const title = isHubNativeWorkOrder(row) ? HUB_NATIVE_INTERNAL_LABOR_COLUMN_TITLE : INTERNAL_LABOR_COLUMN_TITLE;
  const seconds = Number(row.labor_tracked_seconds ?? 0);
  const applied = Number(row.labor_applied_internal_dollars ?? 0);
  const unappliedRaw = row.labor_unapplied_internal_dollars;
  const unapplied = unappliedRaw === undefined ? null : unappliedRaw;
  const ratesConfigured = row.labor_internal_rates_configured === true;
  if (!ratesConfigured && seconds > 0) {
    return {
      label: "Configure rates",
      title: `${title} Open Manufacturing → Settings to set internal labor and overhead dollars per hour.`,
    };
  }
  if (seconds === 0 && applied === 0 && (unapplied === null || unapplied === 0)) {
    return { label: "—", title };
  }
  if (unapplied === null) {
    return { label: `${formatUsdTwoDecimals(applied)} / —`, title };
  }
  return { label: `${formatUsdTwoDecimals(applied)} / ${formatUsdTwoDecimals(unapplied)}`, title };
}

export function formatTrackedScanHours(seconds: number | null | undefined): string {
  const hours = Number(seconds ?? 0) / 3600;
  const safe = Number.isFinite(hours) ? hours : 0;
  return `${safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
}

export const LABOR_UI_DOLLAR_EPS = 0.005;

export function internalLaborTrackedDollars(row: WorkOrderListRow): number | null {
  const raw = row.labor_tracked_internal_dollars;
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function internalLaborAppliedDollars(row: WorkOrderListRow): number {
  return Number(row.labor_applied_internal_dollars ?? 0);
}

/** Megaventory applied internal overhead is above hub-tracked scan dollars (sync down before receive). */
export function internalLaborOverAppliedVsTracked(row: WorkOrderListRow): boolean {
  const tracked = internalLaborTrackedDollars(row);
  if (tracked === null) return false;
  return internalLaborAppliedDollars(row) > tracked + LABOR_UI_DOLLAR_EPS;
}

/** True when sync is useful: tracked dollars known and either unapplied is positive or MV is over-applied. */
export function internalLaborCanSyncInternalOverhead(row: WorkOrderListRow): boolean {
  const tracked = internalLaborTrackedDollars(row);
  if (tracked === null) return false;
  const applied = internalLaborAppliedDollars(row);
  const unappliedRaw = row.labor_unapplied_internal_dollars;
  const unapplied =
    unappliedRaw === undefined || unappliedRaw === null ? null : Number(unappliedRaw);
  const overApplied = applied > tracked + LABOR_UI_DOLLAR_EPS;
  const hasUnapplied = unapplied !== null && Number.isFinite(unapplied) && unapplied > LABOR_UI_DOLLAR_EPS;
  return overApplied || hasUnapplied;
}

/** Megaventory WO number when linked; otherwise hub internal number (hub-native WOs). */
export function workOrderPrimaryLabel(row: Pick<WorkOrderListRow, "number" | "mv_number">): string {
  const megaventoryNumber = (row.mv_number ?? "").trim();
  if (megaventoryNumber !== "") {
    return megaventoryNumber;
  }
  const internalNumber = row.number.trim();
  return internalNumber !== "" ? internalNumber : "—";
}

export function megaventoryUrlFor(
  workOrder: Pick<WorkOrderListRow, "mv_id" | "mv_number">,
): string | null {
  if (workOrder.mv_id == null) return null;
  const name = encodeURIComponent(`Build Order ${workOrder.mv_number ?? ""}`);
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=WorkOrder&id=${workOrder.mv_id}&name=${name}`;
}

export function parseBuildOrderId(raw: string | undefined | null): number | null {
  const parsed = Number.parseInt((raw ?? "").trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Selected work order from path param (preferred) or legacy `?id=` query. */
export function resolveWorkOrderSelectedBuildOrderId(
  pathBuildOrderId: string | undefined,
  legacyIdQuery: string | null,
): number | null {
  const fromPath = parseBuildOrderId(pathBuildOrderId);
  if (fromPath != null) {
    return fromPath;
  }
  return parseBuildOrderId(legacyIdQuery);
}

/** Redirect legacy `?id=` bookmarks to path-based deep links, preserving other query params. */
export function buildLegacyWorkOrderIdRedirectTarget(searchParams: URLSearchParams): string | null {
  const idFromQuery = parseBuildOrderId(searchParams.get("id"));
  if (idFromQuery == null) {
    return null;
  }
  const next = new URLSearchParams(searchParams);
  next.delete("id");
  const queryString = next.toString();
  const base = buildManufacturingWorkOrderDetailPath(idFromQuery);
  return queryString === "" ? base : `${base}?${queryString}`;
}

/** Deep-link into the work orders master–detail page (list + detail panel). */
export function workOrderHubHref(params: { id?: number; number?: string | null; shipmentId?: number }): string {
  const query = new URLSearchParams();
  query.set("status", "all");
  query.set("hide_gso", "0");

  if (params.shipmentId != null && Number.isFinite(params.shipmentId) && params.shipmentId > 0) {
    query.set("shipment_id", String(params.shipmentId));
    return `/manufacturing-management/work-orders?${query.toString()}`;
  }

  const id = params.id != null && Number.isFinite(params.id) && params.id > 0 ? params.id : null;
  const number = params.number?.trim() ?? "";

  if (number.length > 0) {
    query.set("number", number);
  }

  if (id != null) {
    const queryString = query.toString();
    const base = buildManufacturingWorkOrderDetailPath(id);
    return queryString === "" ? base : `${base}?${queryString}`;
  }

  if (number.length > 0) {
    return `/manufacturing-management/work-orders?${query.toString()}`;
  }

  return "/manufacturing-management/work-orders";
}

export function buildQueryParams(
  status: StatusFilter,
  location: LocationFilter,
  hideGso: boolean,
  search: string,
  limit: number,
  shipmentId?: number | null,
): Record<string, string> {
  const params: Record<string, string> = {
    status,
    hideGso: hideGso ? "1" : "0",
    limit: String(limit),
  };
  if (location !== "any") params.location = location;
  const trimmed = search.trim();
  if (trimmed !== "") params.search = trimmed;
  if (shipmentId != null && Number.isFinite(shipmentId) && shipmentId > 0) {
    params.shipment_id = String(shipmentId);
  }
  return params;
}

export const DEFAULT_LIMIT = 500;

export const WORK_ORDERS_PAGE_HELP =
  "Pulled from public.build_order mirrored by the work order ingest. Megaventory is only called when you create, allocate, receive, close, or rebuild. The BOM rebuild queue lists connector-submitted draft rebuilds that need approval; rebuild from the detail panel runs Megaventory immediately.";

export const MANUFACTURING_HUB_READ_ONLY = "Your role has read-only access to the Manufacturing hub module.";
